import React, {useCallback, useEffect, useState} from "react";
import {Box} from "@mui/system";
import {CircularProgress, Divider, Switch, Typography} from "@mui/material";
import _ from "lodash";
import {organizationSettingsService} from "_services";
import {alertMessages, featureFlags} from "_constants";
import {Auth} from "components/Authorization";
import {SnackBar} from "components/common";
import styles from "../organizationMainStyles";
import InformationTooltip from "../Utilities/InformationTooltip";
import {fragmentId} from "../Utilities/organizationSettingsConstants";
import {getLabel} from "../Utilities/organizationSettingsUtils";

// Hints sit right under the section divider, like in Generated Replies
const hintSx = {...styles.infoItem, mt: 0};

function handleRequestError(error) {
    if (_.get(error, "response.status") === 401) {
        Auth.processLogout();
        return;
    }
    const message = _.get(error, "response.data.message") || alertMessages.GENERIC_ERROR;
    SnackBar.showMessage(message);
}

export default function FeatureFlags({organizationId}) {
    const label = getLabel(fragmentId.FEATURE_FLAGS);

    // Toggles in display order
    const toggles = [
        {key: featureFlags.INBOX_2_0, label: label.INBOX_2_0, tooltip: label.INBOX_2_0_TOOLTIP},
    ];

    const [loading, setLoading] = useState(false);
    const [flags, setFlags] = useState(null);
    const [savingKey, setSavingKey] = useState(null);

    const loadFlags = useCallback(async () => {
        if (_.isEmpty(organizationId)) {
            return;
        }
        setLoading(true);
        try {
            const res = await organizationSettingsService.getFeatureFlags(organizationId);
            const loadedFlags = _.get(res, "data.flags", []);
            setFlags(_.fromPairs(loadedFlags.map(flag => [flag.key, flag.enabled])));
        } catch (e) {
            setFlags(null);
            handleRequestError(e);
        } finally {
            setLoading(false);
        }
    }, [organizationId]);

    useEffect(() => {
        loadFlags();
    }, [loadFlags]);

    const handleToggle = async (key, enabled) => {
        setFlags(current => ({...current, [key]: enabled}));
        setSavingKey(key);
        try {
            await organizationSettingsService.updateFeatureFlag(organizationId, {key, enabled});
            SnackBar.showMessage("Feature flag saved.");
        } catch (e) {
            setFlags(current => ({...current, [key]: !enabled}));
            handleRequestError(e);
        } finally {
            setSavingKey(null);
        }
    };

    const renderBody = () => {
        if (_.isEmpty(organizationId)) {
            return <Typography sx={hintSx}>{label.NO_ORGANIZATION_SELECTED}</Typography>;
        }
        if (loading) {
            return (
                <Box sx={styles.bodyLoading}>
                    <CircularProgress sx={styles.bodyLoadingSpinner}/>
                </Box>
            );
        }
        if (!flags) {
            return null;
        }
        return toggles.map(toggle => (
            <Box key={toggle.key} sx={styles.notificationToggleContainer}>
                <Box component={"h2"} sx={styles.notificationToggleLabel}>
                    {toggle.label}
                    <InformationTooltip label={toggle.tooltip}/>
                </Box>
                <Box>
                    <Switch
                        disabled={savingKey === toggle.key}
                        checked={Boolean(flags[toggle.key])}
                        onChange={(event) => handleToggle(toggle.key, event.target.checked)}
                        sx={styles.switch}
                    />
                </Box>
            </Box>
        ));
    };

    return (
        <Box sx={styles.sectionWrapper}>
            <Box component="h2" sx={{...styles.heading, mb: 0}}>
                {label.FEATURE_FLAGS}
            </Box>
            <Divider orientation="horizontal" flexItem sx={styles.sectionDivider}/>
            {renderBody()}
        </Box>
    );
}
